import * as React from 'react'
import { Modal, Button, Input, Label } from '@/shared/ui'
import { Mail } from 'lucide-react'
import { requestPasswordReset } from '../services/passwordRecoveryService'

export type ForgotPasswordModalProps = {
  open: boolean
  onOpenChange: (v: boolean) => void
}

export default function ForgotPasswordModal({ open, onOpenChange }: ForgotPasswordModalProps) {
  const [email, setEmail] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [sent, setSent] = React.useState(false)

  React.useEffect(() => {
    if (!open) {
      setEmail('')
      setSubmitting(false)
      setError(null)
      setSent(false)
    }
  }, [open])

  function validEmail(v: string) {
    return /.+@.+\..+/.test(v)
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!validEmail(email.trim())) {
      setError('Informe um e-mail válido.')
      return
    }
    try {
      setSubmitting(true)
      await requestPasswordReset(email.trim())
      // Mensagem genérica por segurança (evita enumeração de contas)
      setSent(true)
    } catch (err: any) {
      // Resposta genérica mesmo em erro; log local opcional
      console.error('[ForgotPassword] request error', err)
      setSent(true)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange} className="max-w-md">
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Recuperar senha</h2>
          <p className="text-sm text-foreground/70">Informe seu e-mail para enviarmos o link de redefinição.</p>
        </div>

        {sent ? (
          <div className="space-y-4">
            <div className="rounded-md border border-border/60 bg-muted/30 p-3 text-sm">
              Se o e-mail informado estiver cadastrado, você receberá um link para redefinir sua senha.
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => onOpenChange(false)}>Fechar</Button>
            </div>
          </div>
        ) : (
          <form className="space-y-4" onSubmit={onSubmit}>
            {error && (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="fp-email">E-mail</Label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/60" />
                <Input
                  id="fp-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  className="pl-9"
                  required
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => onOpenChange(false)} disabled={submitting}>Cancelar</Button>
              <Button type="submit" disabled={submitting} className="min-w-28">{submitting ? 'Enviando...' : 'Enviar link'}</Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  )
}

